import { useContext, useEffect, useState } from 'react';
import {
  Box, Button, Chip, Divider, FormControl, FormHelperText,
  InputLabel, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import { AddOutlined } from '@mui/icons-material';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import AnalyticReportService from '../../../shared/services/analytic-report/analytic-report.service';
import ScheduleService from '../../../shared/services/schedule/schedule.service';
import DistributionListService from '../../../shared/services/distribution-list/distribution-list.service';
import { ISchedule } from '../../../shared/services/schedule/schedule.model';
import { IDistributionList } from '../../../shared/services/distribution-list/distribution-list.model';
import { ScheduleDialog } from '../settings/schedule-dialog';

const SEND_NOW = '__now__';

interface Props {
  open: boolean;
  onClose: () => void;
  reportId: string;
  reportName: string;
}

export const SendAnalyticsReportDialog = ({ open, onClose, reportId, reportName }: Props) => {
  const analyticReportService = new AnalyticReportService();
  const scheduleService = new ScheduleService();
  const distributionListService = new DistributionListService();

  const userNotificationsContext = useContext(UserNotificationsContext);
  const { userDetails } = useContext(UserDetailsContext);
  const isAdmin = userDetails?.roles?.includes('admin') ?? false;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [distributionListIds, setDistributionListIds] = useState<string[]>([]);
  const [additionalEmails, setAdditionalEmails] = useState('');
  const [customEmailText, setCustomEmailText] = useState('Your report is attached to this email.');
  const [sendTiming, setSendTiming] = useState<string>(SEND_NOW);
  const [distributionLists, setDistributionLists] = useState<IDistributionList[]>([]);
  const [schedules, setSchedules] = useState<ISchedule[]>([]);
  const [openCreateSchedule, setOpenCreateSchedule] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadSchedules = () =>
    scheduleService.getAll({ perPage: 100, page: 0 })
      .then(r => setSchedules(r.rows))
      .catch(() => setSchedules([]));

  useEffect(() => {
    if (open) {
      setDistributionListIds([]);
      setAdditionalEmails('');
      setCustomEmailText('Your report is attached to this email.');
      setSendTiming(SEND_NOW);
      setErrors({});
      distributionListService.getAll()
        .then(r => setDistributionLists(r))
        .catch(() => setDistributionLists([]));
      loadSchedules();
    }
  }, [open]);

  const parsedEmails = additionalEmails
    .split(/[\n,]/)
    .map(e => e.trim())
    .filter(e => e && e.includes('@'));

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (distributionListIds.length === 0 && parsedEmails.length === 0) {
      errs.recipients = 'At least one distribution list or email address is required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      if (sendTiming === SEND_NOW) {
        const result = await analyticReportService.send(reportId, {
          distributionListIds,
          additionalEmails: parsedEmails,
          customEmailText,
        });
        userNotificationsContext.openSnackbar({
          open: true,
          message: `Report queued for ${result.queued} recipient${result.queued === 1 ? '' : 's'}`,
          backgroundColor: 'primary.dark',
        });
      } else {
        await scheduleService.createAction(sendTiming, {
          actionType: 'send_analytics_report',
          actionPayload: {
            analyticReportId: reportId,
            distributionListIds,
            additionalEmails: parsedEmails,
            customEmailText,
          },
        });
        const schedule = schedules.find(s => s.id === sendTiming);
        userNotificationsContext.openSnackbar({
          open: true,
          message: `Report added to schedule "${schedule?.name ?? ''}"`,
          backgroundColor: 'primary.dark',
        });
      }
      onClose();
    } catch {
      userNotificationsContext.openSnackbar({
        open: true,
        message: 'An error occurred, please try again',
        backgroundColor: 'text.primary',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScheduleCreated = async (newSchedule: ISchedule) => {
    setOpenCreateSchedule(false);
    await loadSchedules();
    setSendTiming(newSchedule.id);
  };

  return (
    <>
      <DialogWrapper
        open={open}
        onClose={onClose}
        title='Send Analytics Report'
        cancelHandler={onClose}
        submitHandler={handleSubmit}
        isSubmitting={isSubmitting}
        disableSaveButton={isSubmitting}
        maxWidth='sm'
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            Send <strong>{reportName}</strong> as a PDF attachment by email.
          </Typography>

          {/* Distribution lists */}
          <FormControl size='small' fullWidth error={!!errors.recipients}>
            <InputLabel>Distribution lists</InputLabel>
            <Select
              label='Distribution lists'
              multiple
              value={distributionListIds}
              onChange={e => {
                setDistributionListIds(e.target.value as string[]);
                if (errors.recipients) setErrors(prev => ({ ...prev, recipients: '' }));
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map(id => {
                    const list = distributionLists.find(l => l.id === id);
                    return <Chip key={id} label={list?.listName ?? id} size='small' />;
                  })}
                </Box>
              )}
            >
              {distributionLists.length === 0 && (
                <MenuItem disabled>No distribution lists available</MenuItem>
              )}
              {distributionLists.map(l => (
                <MenuItem key={l.id} value={l.id}>{l.listName}</MenuItem>
              ))}
            </Select>
            {errors.recipients && <FormHelperText>{errors.recipients}</FormHelperText>}
          </FormControl>

          {/* Additional emails */}
          <TextField
            size='small'
            label='Additional email addresses (optional)'
            value={additionalEmails}
            onChange={e => {
              setAdditionalEmails(e.target.value);
              if (errors.recipients) setErrors(prev => ({ ...prev, recipients: '' }));
            }}
            fullWidth
            multiline
            rows={3}
            placeholder={'jane@example.com\njohn@example.com'}
            helperText={
              parsedEmails.length > 0
                ? `${parsedEmails.length} valid email${parsedEmails.length === 1 ? '' : 's'} detected`
                : 'One email address per line'
            }
          />

          {/* Email body text */}
          <TextField
            size='small'
            label='Email body text'
            value={customEmailText}
            onChange={e => setCustomEmailText(e.target.value)}
            fullWidth
            multiline
            rows={3}
            helperText='Text shown in the email body'
          />

          <Divider />

          {/* Send timing */}
          <Typography variant='subtitle2' fontWeight={600}>When to send</Typography>

          <FormControl size='small' fullWidth>
            <InputLabel>Send timing</InputLabel>
            <Select
              label='Send timing'
              value={sendTiming}
              onChange={e => setSendTiming(e.target.value)}
            >
              <MenuItem value={SEND_NOW}>Send immediately</MenuItem>
              {schedules.map(s => {
                const hasPendingRun = s.active && !!s.nextRunAt;
                return (
                  <MenuItem key={s.id} value={s.id} disabled={!hasPendingRun}>
                    {s.name}
                    {hasPendingRun ? (
                      <Typography variant='caption' color='text.secondary' sx={{ ml: 1 }}>
                        (next: {new Date(s.nextRunAt!).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })})
                      </Typography>
                    ) : (
                      <Typography variant='caption' color='text.disabled' sx={{ ml: 1 }}>
                        No pending runs
                      </Typography>
                    )}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {isAdmin && (
            <Button
              size='small'
              variant='text'
              startIcon={<AddOutlined />}
              onClick={() => setOpenCreateSchedule(true)}
              sx={{ alignSelf: 'flex-start' }}
            >
              Create new schedule
            </Button>
          )}

          {sendTiming !== SEND_NOW && (
            <Typography variant='caption' color='text.secondary'>
              The report will be generated and sent when this schedule next fires.
            </Typography>
          )}
        </Stack>
      </DialogWrapper>

      <ScheduleDialog
        open={openCreateSchedule}
        onClose={() => setOpenCreateSchedule(false)}
        successHandler={() => {}}
        defaultActions={[{
          actionType: 'send_analytics_report',
          analyticReportId: reportId,
          distributionListIds,
          additionalEmails,
          customEmailText,
        }]}
        onCreated={handleScheduleCreated}
      />
    </>
  );
};
